/**
 * Notifications Store
 * Manages live notifications for VMS system
 */

import { defineStore } from 'pinia'

export interface VMSNotification {
  id: string
  title: string
  message: string
  type: 'success' | 'info' | 'warning' | 'error'
  timestamp: Date
  isRead: boolean
  vehicleId?: string
  vehicleTitle?: string
  category: 'vehicle_update' | 'price_change' | 'new_vehicle' | 'vehicle_removed' | 'system' | 'connection'
  icon?: string
}

interface NotificationsState {
  notifications: VMSNotification[]
  maxNotifications: number
  unreadCount: number
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    notifications: [],
    maxNotifications: 50, // Keep last 50 notifications
    unreadCount: 0
  }),

  getters: {
    // Get all notifications ordered by newest first
    allNotifications: (state) => {
      return state.notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    },

    // Get unread notifications
    unreadNotifications: (state) => {
      return state.notifications.filter(n => !n.isRead)
    },

    // Get notifications by category
    notificationsByCategory: (state) => (category: VMSNotification['category']) => {
      return state.notifications.filter(n => n.category === category)
    },

    // Get recent notifications (last 10)
    recentNotifications: (state) => {
      return state.notifications
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 10)
    }
  },

  actions: {
    /**
     * Add a new notification
     */
    addNotification(notification: Omit<VMSNotification, 'id' | 'timestamp' | 'isRead'>) {
      const newNotification: VMSNotification = {
        ...notification,
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        isRead: false
      }

      this.notifications.unshift(newNotification)
      this.unreadCount++

      // Trim notifications if we exceed max
      if (this.notifications.length > this.maxNotifications) {
        const removed = this.notifications.splice(this.maxNotifications)
        // Adjust unread count if we removed unread notifications
        const removedUnread = removed.filter(n => !n.isRead).length
        this.unreadCount = Math.max(0, this.unreadCount - removedUnread)
      }

      return newNotification
    },

    /**
     * Mark notification as read
     */
    markAsRead(notificationId: string) {
      const notification = this.notifications.find(n => n.id === notificationId)
      if (notification && !notification.isRead) {
        notification.isRead = true
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }
    },

    /**
     * Mark all notifications as read
     */
    markAllAsRead() {
      this.notifications.forEach(n => {
        n.isRead = true
      })
      this.unreadCount = 0
    },

    /**
     * Remove a specific notification
     */
    removeNotification(notificationId: string) {
      const index = this.notifications.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        const notification = this.notifications[index]
        if (!notification.isRead) {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
        this.notifications.splice(index, 1)
      }
    },

    /**
     * Clear all notifications
     */
    clearAllNotifications() {
      this.notifications = []
      this.unreadCount = 0
    },

    /**
     * Clear old notifications (older than X days)
     */
    clearOldNotifications(daysOld: number = 7) {
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - daysOld)

      const oldNotifications = this.notifications.filter(n => n.timestamp < cutoffDate)
      const oldUnreadCount = oldNotifications.filter(n => !n.isRead).length

      this.notifications = this.notifications.filter(n => n.timestamp >= cutoffDate)
      this.unreadCount = Math.max(0, this.unreadCount - oldUnreadCount)
    },

    /**
     * Add vehicle-related notification
     */
    addVehicleNotification(
      title: string,
      message: string,
      vehicleId: string,
      vehicleTitle: string,
      category: 'vehicle_update' | 'price_change' | 'new_vehicle' | 'vehicle_removed',
      type: VMSNotification['type'] = 'info'
    ) {
      const icons = {
        vehicle_update: '🚗',
        price_change: '💰',
        new_vehicle: '✨',
        vehicle_removed: '❌'
      }

      return this.addNotification({
        title,
        message,
        type,
        vehicleId,
        vehicleTitle,
        category,
        icon: icons[category]
      })
    },

    /**
     * Add system notification
     */
    addSystemNotification(
      title: string,
      message: string,
      type: VMSNotification['type'] = 'info'
    ) {
      return this.addNotification({
        title,
        message,
        type,
        category: 'system',
        icon: type === 'error' ? '⚠️' : type === 'success' ? '✅' : 'ℹ️'
      })
    },

    /**
     * Add connection notification
     */
    addConnectionNotification(
      title: string,
      message: string,
      type: VMSNotification['type'] = 'info'
    ) {
      return this.addNotification({
        title,
        message,
        type,
        category: 'connection',
        icon: type === 'success' ? '🚀' : type === 'error' ? '❌' : '🔄'
      })
    }
  }
})